/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from '@/lib/utils';
import React, { useState, createContext, useContext, useEffect, ReactNode } from 'react';
import { CheckGroupIcon } from '../ui/icon';

// 定义上下文类型
interface CheckboxGroupContextType {
  checkedValues: string[];
  handleChange: (value: string) => void;
  name: string;
  disabled: boolean;
}

// 创建带类型的上下文（允许undefined，后续会校验）
const CheckboxGroupContext = createContext<CheckboxGroupContextType | undefined>(undefined);

// 定义勾选动画样式
const styles = `
  @keyframes checkmark {
    0% {
      stroke-dasharray: 0 10;
      stroke-dashoffset: 10;
    }
    100% {
      stroke-dasharray: 10 0;
      stroke-dashoffset: 0;
    }
  }
`;

// 根组件属性类型
interface CheckboxGroupRootProps {
  children: ReactNode;
  defaultValue?: string[]; // 改为可选，修复"应有1个参数"错误
  value?: string[];
  onChange?: (values: string[]) => void;
  name: string; // 表单名称必填
  disabled?: boolean;
  className?: string;
  [key: string]: any; // 允许其他HTML属性
}

// 子项属性类型
interface CheckboxGroupItemProps {
  value: string;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  [key: string]: any; // 允许其他HTML属性
}

const CheckboxGroup = {
  /** 复选框组容器组件 */
  Root: ({
    children,
    defaultValue = [],
    value: controlledValue,
    onChange,
    name,
    disabled = false,
    className = "",
    ...props
  }: CheckboxGroupRootProps) => {
    // 状态管理：支持受控和非受控模式
    const [checkedValues, setCheckedValues] = useState<string[]>(defaultValue);

    // 受控模式同步
    useEffect(() => {
      if (controlledValue !== undefined) {
        setCheckedValues(controlledValue);
      }
    }, [controlledValue]);

    // 默认值同步
    useEffect(() => {
      if (defaultValue.length) {
        setCheckedValues(defaultValue);
      }
    }, [defaultValue]);

    // 处理选项变更
    const handleChange = (value: string) => {
      if (disabled) return;

      const newValues = checkedValues.includes(value)
        ? checkedValues.filter(v => v !== value)
        : [...checkedValues, value];

      setCheckedValues(newValues);
      onChange && onChange(newValues);
    };

    // 提供上下文值（严格符合类型定义）
    const contextValue: CheckboxGroupContextType = {
      checkedValues,
      handleChange,
      name,
      disabled
    };

    return (
      <>
        <CheckboxGroupContext.Provider value={contextValue}>
          <div
            className={`${className}`}
            role="group"
            aria-disabled={disabled}
            {...props}
          >
            {children}
          </div>
        </CheckboxGroupContext.Provider>
      </>
    );
  },


  /** 单个复选框组件 */
  Item: ({
    value,
    children,
    disabled: itemDisabled = false,
    className = "",
    ...props
  }: CheckboxGroupItemProps) => {
    // 获取上下文并校验
    const context = useContext(CheckboxGroupContext);


    if (!context) {
      throw new Error('CheckboxGroup.Item 必须在 CheckboxGroup.Root 内部使用');
    }

    // 从上下文解构
    const { checkedValues, handleChange, name, disabled: groupDisabled } = context;
    const isChecked = checkedValues.includes(value);
    const isDisabled = groupDisabled || itemDisabled;

    return (
      <label
        className={cn(
          'flex items-center gap-2 cursor-pointer',
          isDisabled ? 'opacity-60 cursor-not-allowed' : '',
          className
        )}
        style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
      >
        <div className="relative">
          {/* 实际的复选框输入元素 */}
          <input
            type="checkbox"
            name={name}
            value={value}
            checked={isChecked}
            onChange={() => !isDisabled && handleChange(value)}
            disabled={isDisabled}
            className="sr-only peer"
            aria-checked={isChecked}
            {...props}
          />

          {/* 自定义复选框样式 */}
          <div
            className={cn(
              'size-5 rounded-[6px] transition-all duration-200 ease-in-out flex flex-shrink-0 items-center justify-center',
              // 禁用状态下的样式
              isDisabled && 'bg-[#F8F8F8] border border-[#70747E]',
              // 选中状态下的样式
              isChecked ? 'bg-[#F3F3F4] border border-[#101828]' : 'border border-[#D0D5DD]',
              // 禁用状态下选中项的样式
              (isDisabled && isChecked) && 'bg-[#F8F8F8]'
            )}
          >
            {/* 勾选标记 */}
            {isChecked && (
              <CheckGroupIcon className={cn(isDisabled && "text-[#70747E]")} />
            )}
          </div>
        </div>

        {/* 选项文本 */}
        <span
          className={cn(
            "text-[rgba(52,64,84,1)] font-normal transition-colors duration-200",
            isDisabled && 'text-[rgba(29,41,57,1)]',
          )}
        >
          {children}
        </span>
      </label>
    );
  }
};

export default CheckboxGroup;